import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight, CalendarClock, MessageCircle, TrendingUp, Sparkles, AlertTriangle, Camera } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, YAxis, ReferenceDot } from 'recharts';
import { useClientSettings, accentClasses } from './settings';
import ReceiptSnap from './ReceiptSnap';

const healthHistory = [
  { m: 'Nov', score: 64 },
  { m: 'Dec', score: 68 },
  { m: 'Jan', score: 71 },
  { m: 'Feb', score: 74 },
  { m: 'Mar', score: 78 },
  { m: 'Apr', score: 82 },
];

const healthScore = 82;
const previousScore = 78;
const scoreChange = healthScore - previousScore;

const nextDeadline = {
  title: 'Q3 BAS lodgement',
  date: '28 Apr 2026',
  daysAway: 12,
};

const latestMessage = {
  from: 'Marcus Bell',
  preview: "I've reviewed your March P&L — looks good. One quick question about the new equipment purchase…",
  time: '2 hours ago',
  unread: true,
};

const quickStats = [
  { label: 'YTD Revenue', value: '$284,500', delta: '+12.4%', positive: true },
  { label: 'Net Profit', value: '$48,200', delta: '+8.1%', positive: true },
];

export default function HomePage() {
  const { settings, t } = useClientSettings();
  const accent = accentClasses[settings.accent];
  const [receiptOpen, setReceiptOpen] = useState(false);

  return (
    <div className="space-y-5 pt-2">
      {/* Weekly briefing — Monday morning "wow" moment */}
      <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="text-xs font-medium uppercase tracking-wider text-primary">This week's briefing</p>
              <span className="text-[10px] text-muted-foreground">Mon 28 Apr</span>
            </div>
            <p className="mt-1 text-sm leading-relaxed text-foreground">
              Last week was your <span className="font-semibold">strongest month-to-date</span> — revenue $32,400 (up 14% on the previous week). 3 customers paid promptly.
            </p>
            <div className="mt-3 flex items-start gap-2 rounded-xl bg-amber-500/10 p-3">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-700 dark:text-amber-400" />
              <p className="text-xs leading-relaxed text-foreground">
                <span className="font-semibold">Worth chasing:</span> Acme Builders owes $4,200 (overdue 12 days). Tap to send a reminder.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Health score hero — sparkline trend */}
      <div className={`rounded-3xl bg-gradient-to-br ${accent.gradient} p-6 text-white shadow-lg shadow-black/10`}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-white/80">{t('business_health')}</p>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-3xl font-bold">{healthScore}</span>
              <span className="text-xs text-white/70">/ 100 · trending up</span>
            </div>
          </div>
          <div className="flex items-center gap-1 rounded-full bg-white/15 px-3 py-1.5 text-sm">
            <ArrowUpRight className="h-4 w-4" />
            <span className="font-medium">+{scoreChange}</span>
          </div>
        </div>

        {/* Sparkline */}
        <div className="-mx-2 mt-3 h-20">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={healthHistory} margin={{ top: 8, right: 16, left: 16, bottom: 0 }}>
              <YAxis hide domain={[40, 100]} />
              <defs>
                <linearGradient id="sparkLine" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="rgba(255,255,255,0.4)" />
                  <stop offset="100%" stopColor="rgba(255,255,255,1)" />
                </linearGradient>
              </defs>
              <Line
                type="monotone"
                dataKey="score"
                stroke="url(#sparkLine)"
                strokeWidth={3}
                dot={{ r: 0 }}
                activeDot={false}
                isAnimationActive={true}
              />
              <ReferenceDot
                x={healthHistory[healthHistory.length - 1].m}
                y={healthHistory[healthHistory.length - 1].score}
                r={6}
                fill="#fff"
                stroke="rgba(255,255,255,0.5)"
                strokeWidth={4}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="-mt-1 flex justify-between px-3 text-[10px] text-white/60">
          {healthHistory.map(p => <span key={p.m}>{p.m}</span>)}
        </div>

        <Link
          to="/client-pwa/insights"
          className="mt-4 flex items-center justify-between rounded-2xl bg-white/15 px-4 py-3 text-sm font-medium transition-colors hover:bg-white/25"
        >
          <span>See what's driving this</span>
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 gap-3">
        {quickStats.map(s => (
          <div key={s.label} className="rounded-2xl border border-border bg-card p-4">
            <p className="text-xs font-medium text-muted-foreground">{s.label}</p>
            <p className="mt-1 text-xl font-bold text-foreground">{s.value}</p>
            <div className={`mt-1 flex items-center gap-0.5 text-xs font-medium ${s.positive ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
              <TrendingUp className="h-3 w-3" />
              <span>{s.delta}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Next deadline */}
      <Link
        to="/client-pwa/files"
        className="block rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 transition-colors hover:bg-amber-500/15"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-amber-500 text-white">
            <CalendarClock className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-medium uppercase tracking-wider text-amber-700 dark:text-amber-400">
              {t('next_deadline')} · {nextDeadline.daysAway} days
            </p>
            <p className="text-sm font-semibold text-foreground">{nextDeadline.title}</p>
            <p className="text-xs text-muted-foreground">{nextDeadline.date}</p>
          </div>
          <ArrowRight className="h-4 w-4 text-amber-700 dark:text-amber-400" />
        </div>
      </Link>

      {/* Latest message */}
      <div>
        <div className="mb-2 flex items-center justify-between px-1">
          <h2 className="text-sm font-semibold text-foreground/80">{t('latest_from_accountant')}</h2>
          {latestMessage.unread && (
            <span className="flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              New
            </span>
          )}
        </div>
        <Link
          to="/client-pwa/help"
          className="block rounded-2xl border border-border bg-card p-4 transition-colors hover:border-muted-foreground/30"
        >
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-muted text-sm font-medium text-foreground">
              HV
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <p className="truncate text-sm font-semibold text-foreground">{latestMessage.from}</p>
                <p className="text-xs text-muted-foreground">{latestMessage.time}</p>
              </div>
              <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground line-clamp-2">{latestMessage.preview}</p>
            </div>
          </div>
        </Link>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-3 pt-1">
        <button
          onClick={() => setReceiptOpen(true)}
          className="flex flex-col items-start rounded-2xl border border-primary/30 bg-primary/5 p-4 text-left transition-colors hover:bg-primary/10"
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <Camera className="h-4 w-4" />
          </div>
          <p className="mt-2 text-sm font-semibold text-foreground">Snap a receipt</p>
          <p className="text-xs text-muted-foreground">AI fills the rest</p>
        </button>
        <Link
          to="/client-pwa/help"
          className="rounded-2xl border border-border bg-card p-4 transition-colors hover:border-muted-foreground/30"
        >
          <MessageCircle className="h-5 w-5 text-foreground" />
          <p className="mt-2 text-sm font-semibold text-foreground">{t('ask_a_question')}</p>
          <p className="text-xs text-muted-foreground">{t('ai_replies_in_seconds')}</p>
        </Link>
      </div>

      <ReceiptSnap open={receiptOpen} onClose={() => setReceiptOpen(false)} />
    </div>
  );
}
